//
// Flexible micronutrient model: a Micros map is canonical-key → amount in that
// key's display unit (unit is baked into the key name, e.g. vit_c_mg = mg).
// The MICRO_KEYS registry is the single source of truth for which micros exist,
// how they are labelled/units, and how to read them from Open Food Facts.

export type Micros = Record<string, number>;

export interface MicroDef {
  key: string;
  label_en: string;
  label_de: string;
  unit: string;       // display unit, e.g. "mg" | "µg"
  off_field: string;  // Open Food Facts nutriment key (value is in grams per 100g)
  off_factor: number; // grams → display unit multiplier (1000 for mg, 1e6 for µg)
}

export const MICRO_KEYS: MicroDef[] = [
  { key: "vit_c_mg",     label_en: "Vitamin C", label_de: "Vitamin C", unit: "mg", off_field: "vitamin-c_100g", off_factor: 1000 },
  { key: "vit_d_ug",     label_en: "Vitamin D", label_de: "Vitamin D", unit: "µg", off_field: "vitamin-d_100g", off_factor: 1_000_000 },
  { key: "vit_a_ug",     label_en: "Vitamin A", label_de: "Vitamin A", unit: "µg", off_field: "vitamin-a_100g", off_factor: 1_000_000 },
  { key: "calcium_mg",   label_en: "Calcium",   label_de: "Kalzium",   unit: "mg", off_field: "calcium_100g",   off_factor: 1000 },
  { key: "iron_mg",      label_en: "Iron",      label_de: "Eisen",     unit: "mg", off_field: "iron_100g",      off_factor: 1000 },
  { key: "potassium_mg", label_en: "Potassium", label_de: "Kalium",    unit: "mg", off_field: "potassium_100g", off_factor: 1000 },
  { key: "magnesium_mg", label_en: "Magnesium", label_de: "Magnesium", unit: "mg", off_field: "magnesium_100g", off_factor: 1000 },
  { key: "zinc_mg",      label_en: "Zinc",      label_de: "Zink",      unit: "mg", off_field: "zinc_100g",      off_factor: 1000 },
];

export const MICRO_KEY_SET = new Set(MICRO_KEYS.map((d) => d.key));

const MAX_MICRO = 100_000; // generous per-100g/portion ceiling; guards the global cache

const round1 = (n: number): number => Math.round(n * 10) / 10;

function toNum(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = parseFloat(v.replace(",", "."));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function sanitizeMicros(raw: unknown): Micros | null {
  if (!raw || typeof raw !== "object") return null;
  const out: Micros = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!MICRO_KEY_SET.has(k)) continue;
    const n = toNum(v);
    if (n === null || n < 0 || n > MAX_MICRO) continue;
    out[k] = round1(n);
  }
  return Object.keys(out).length ? out : null;
}

export function scaleMicros(m: Micros | null, factor: number): Micros | null {
  if (!m) return null;
  const out: Micros = {};
  for (const [k, v] of Object.entries(m)) {
    const scaled = round1(v * factor);
    if (scaled > 0) out[k] = scaled;
  }
  return Object.keys(out).length ? out : null;
}

export function mergeMicros(list: Array<Micros | null | undefined>): Micros {
  const out: Micros = {};
  for (const m of list) {
    if (!m) continue;
    for (const [k, v] of Object.entries(m)) {
      if (!MICRO_KEY_SET.has(k)) continue;
      out[k] = round1((out[k] ?? 0) + v);
    }
  }
  return out;
}

export function parseMicrosJson(s: string | null | undefined): Micros | null {
  if (!s) return null;
  try { return sanitizeMicros(JSON.parse(s)); }
  catch { return null; }
}

export function serializeMicros(m: Micros | null | undefined): string | null {
  const clean = sanitizeMicros(m);
  return clean ? JSON.stringify(clean) : null;
}

export function extractOffMicros(n: Record<string, unknown>): Micros | null {
  const out: Micros = {};
  for (const def of MICRO_KEYS) {
    const grams = toNum(n[def.off_field]);
    if (grams === null || grams < 0) continue;
    const val = round1(grams * def.off_factor);
    if (val > 0) out[def.key] = val;
  }
  return Object.keys(out).length ? out : null;
}
